// User Models - Aligned with ENTITY_RELATIONSHIPS.md

export type UserRole = 'client' | 'cleaner' | 'admin';
export type Language = 'en' | 'nl' | 'fr';
export type CommissionTier = 'promotional' | 'bronze' | 'silver' | 'gold';
export type CleanerApprovalStatus = 'pending_setup' | 'pending_admin_review' | 'active' | 'rejected';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  preferredLanguage?: Language;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  /** Set when the user has 3+ blocked chat filter attempts. */
  chatFilterWarningAt?: Date;
  lastLoginAt?: Date;
  emailVerified: boolean;
  stripeCustomerId?: string;
  stripeAccountId?: string;
}

export interface Address {
  street: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  country: string;
  province?: string;
  latitude?: number;
  longitude?: number;
}

export interface ClientProfile {
  userId: string;
  companyName?: string;
  vatNumber?: string;
  isB2B: boolean;
  billingAddress: Address;
  defaultServiceAddress?: Address;
  preferredLanguage: Language;
  paymentMethodId?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CleanerProfile {
  userId: string;
  bio?: string;
  spokenLanguages?: Language[];
  hourlyRateB2C: number;
  fixedRateB2B?: number;
  isAvailable: boolean;
  profileVisibility: boolean;
  approvalStatus: CleanerApprovalStatus;
  approvedAt?: Date;
  approvedBy?: string;
  rejectionReason?: string;
  baseLocation?: Address;
  maxTravelDistance: number; // in km
  kmCompensation?: number;
  kmCompensationIncluded: boolean;
  serviceAreas: string[];
  serviceCategories: ServiceCategory[];
  serviceSubcategories: ServiceSubcategory[];
  rating?: number;
  reviewCount: number;
  totalJobsCompleted: number;
  commissionTier: CommissionTier;
  iban?: string;
  /** Legal name on the bank account; must match the IBAN holder for payouts. */
  accountHolderName?: string;
  vatNumber?: string;
  vatExempt?: boolean;
  businessName?: string;
  businessAddress?: Address;
  insuranceExpiryDate?: Date;
  insuranceVerified: boolean;
  stripeAccountId?: string;
  stripeAccountStatus?: 'pending' | 'active' | 'restricted';
  onboardingProgress?: OnboardingProgress;
  documents: CleanerDocument[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface OnboardingProgress {
  businessDetails: boolean;
  insuranceCertificate: boolean;
  payoutDetails: boolean;
  pricingSetup: boolean;
  specializations: boolean;
  serviceArea: boolean;
  profileCompletion: boolean;
}

export interface CleanerDocument {
  id: string;
  cleanerId: string;
  type: 'id' | 'insurance' | 'certificate' | 'vat_proof';
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt: Date;
  verified: boolean;
  verifiedAt?: Date;
  verifiedBy?: string;
  rejectionReason?: string;
  expiryDate?: Date;
}

export enum ServiceCategory {
  RESIDENTIAL = 'RESIDENTIAL',
  BUSINESS = 'BUSINESS',
}

// Residential subcategories (B2C) — home cleaning distribution
export enum ResidentialSubcategory {
  REGULAR_CLEANING = 'REGULAR_CLEANING',
  DEEP_CLEANING = 'DEEP_CLEANING',
  WINDOW_CLEANING = 'WINDOW_CLEANING',
  RESIDENTIAL_SOLAR_PANELS = 'RESIDENTIAL_SOLAR_PANELS',
  RESIDENTIAL_GUTTER_CLEANING = 'RESIDENTIAL_GUTTER_CLEANING',
  CAR_DETAILING = 'CAR_DETAILING',
  /** @deprecated Legacy rows only — not offered in new requests */
  MOVE_IN_OUT = 'MOVE_IN_OUT',
  /** @deprecated Legacy rows only */
  IRONING_SERVICE = 'IRONING_SERVICE',
}

// Business subcategories (B2B) — office cleaning distribution
export enum BusinessSubcategory {
  OFFICE_CLEANING = 'OFFICE_CLEANING',
  BUSINESS_DEEP_CLEANING = 'BUSINESS_DEEP_CLEANING',
  BUSINESS_WINDOWS = 'BUSINESS_WINDOWS',
  BUSINESS_SOLAR_PANELS = 'BUSINESS_SOLAR_PANELS',
}

export type ServiceSubcategory = ResidentialSubcategory | BusinessSubcategory;

// Service category display names
export const SERVICE_CATEGORY_LABELS: Record<ServiceCategory, string> = {
  [ServiceCategory.RESIDENTIAL]: 'Home cleaning (B2C)',
  [ServiceCategory.BUSINESS]: 'Office cleaning (B2B)',
};

/** i18n keys for ngx-translate */
export const SERVICE_CATEGORY_LABEL_KEYS: Record<ServiceCategory, string> = {
  [ServiceCategory.RESIDENTIAL]: 'serviceCategory.residential',
  [ServiceCategory.BUSINESS]: 'serviceCategory.business',
};

export const RESIDENTIAL_SUBCATEGORY_LABELS: Record<ResidentialSubcategory, string> = {
  [ResidentialSubcategory.REGULAR_CLEANING]: 'Regular cleaning',
  [ResidentialSubcategory.DEEP_CLEANING]: 'Deep cleaning',
  [ResidentialSubcategory.WINDOW_CLEANING]: 'Window cleaning',
  [ResidentialSubcategory.RESIDENTIAL_SOLAR_PANELS]: 'Solar panel cleaning',
  [ResidentialSubcategory.RESIDENTIAL_GUTTER_CLEANING]: 'Gutter cleaning',
  [ResidentialSubcategory.CAR_DETAILING]: 'Car detailing',
  [ResidentialSubcategory.MOVE_IN_OUT]: 'Move-in/out Cleaning',
  [ResidentialSubcategory.IRONING_SERVICE]: 'Ironing Service',
};

export const RESIDENTIAL_SUBCATEGORY_LABEL_KEYS: Record<ResidentialSubcategory, string> = {
  [ResidentialSubcategory.REGULAR_CLEANING]: 'services.residential.regular.label',
  [ResidentialSubcategory.DEEP_CLEANING]: 'services.residential.deep.label',
  [ResidentialSubcategory.WINDOW_CLEANING]: 'services.residential.windowCleaning.label',
  [ResidentialSubcategory.RESIDENTIAL_SOLAR_PANELS]: 'services.residential.solarPanels.label',
  [ResidentialSubcategory.RESIDENTIAL_GUTTER_CLEANING]: 'services.residential.gutterCleaning.label',
  [ResidentialSubcategory.CAR_DETAILING]: 'onboarding.services.car.label',
  [ResidentialSubcategory.MOVE_IN_OUT]: 'serviceSubcategory.residential.moveInOut',
  [ResidentialSubcategory.IRONING_SERVICE]: 'serviceSubcategory.residential.ironingService',
};

export const BUSINESS_SUBCATEGORY_LABELS: Record<BusinessSubcategory, string> = {
  [BusinessSubcategory.OFFICE_CLEANING]: 'Business cleaning',
  [BusinessSubcategory.BUSINESS_DEEP_CLEANING]: 'Business deep cleaning',
  [BusinessSubcategory.BUSINESS_WINDOWS]: 'Business window cleaning',
  [BusinessSubcategory.BUSINESS_SOLAR_PANELS]: 'Business solar panel cleaning',
};

export const BUSINESS_SUBCATEGORY_LABEL_KEYS: Record<BusinessSubcategory, string> = {
  [BusinessSubcategory.OFFICE_CLEANING]: 'services.business.office.label',
  [BusinessSubcategory.BUSINESS_DEEP_CLEANING]: 'services.business.deep.label',
  [BusinessSubcategory.BUSINESS_WINDOWS]: 'services.business.windows.label',
  [BusinessSubcategory.BUSINESS_SOLAR_PANELS]: 'services.business.solarPanels.label',
};
